import type { CatalogVariation } from '@/lib/api/catalog/catalog-api'
import { formatCentsLabel } from '../scheduling-format'
import type { ConsultationVariationItem } from '../types'

interface ConsultationVariationsPickerProps {
    variations: CatalogVariation[]
    selected: ConsultationVariationItem[]
    /**
     * Preço da variação para o parceiro/profissional escolhidos no agendamento
     * (cascata `parceiro > profissional > empresa`, resolvida pela página).
     */
    priceOf: (variation: CatalogVariation) => number
    /**
     * Menor preço cadastrado em algum vínculo, quando a escolha atual não tem
     * preço — vira o "a partir de".
     */
    priceFromOf: (variation: CatalogVariation) => number | null
    onToggle: (variation: CatalogVariation, checked: boolean) => void
}

/**
 * Variações da consulta — multi-seleção.
 *
 * Consulta escolhe por caixa de seleção (várias ao mesmo tempo); exame e
 * procedimento escolhem uma por linha da pilha. Por isso este componente é só da
 * consulta e não do `SelectedItemsTable`.
 */
export default function ConsultationVariationsPicker({
    variations,
    selected,
    priceOf,
    priceFromOf,
    onToggle,
}: ConsultationVariationsPickerProps) {
    if (variations.length === 0) return null

    return (
        <div className="rounded-xl border border-base-300 bg-base-200/40 p-4">
            <h3 className="text-sm font-semibold text-base-content">
                Variações
            </h3>
            <p className="mb-3 mt-1 text-xs leading-relaxed text-base-content/50">
                Selecione a variação. É possível escolher mais de uma — o valor
                das variações substitui o preço base da consulta.
            </p>
            <div className="flex flex-col gap-1.5">
                {variations.map(variation => {
                    const checked = selected.some(
                        item => item.catalogVariationId === variation.id
                    )
                    const price = priceOf(variation)
                    const priceFrom = priceFromOf(variation)
                    return (
                        <label
                            key={variation.id}
                            className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2 transition-colors ${
                                checked
                                    ? 'border-secondary/30 bg-secondary/5'
                                    : 'border-transparent hover:bg-base-200'
                            }`}
                        >
                            <span className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-sm"
                                    checked={checked}
                                    onChange={e =>
                                        onToggle(variation, e.target.checked)
                                    }
                                />
                                <span className="font-medium">
                                    {variation.name}
                                </span>
                            </span>
                            {price > 0 ? (
                                <span className="text-sm font-medium text-secondary">
                                    {formatCentsLabel(price)}
                                </span>
                            ) : (
                                /* Sem preço para a escolha atual: diz de onde ele
                                   vem, em vez de mostrar R$ 0,00 — que se lê como
                                   "de graça" ou "quebrado". */
                                <span className="text-right text-xs text-base-content/50">
                                    {priceFrom != null ? (
                                        <>
                                            a partir de{' '}
                                            {formatCentsLabel(priceFrom)}
                                            <span className="block">
                                                escolha o parceiro
                                            </span>
                                        </>
                                    ) : (
                                        'sem preço cadastrado'
                                    )}
                                </span>
                            )}
                        </label>
                    )
                })}
            </div>
        </div>
    )
}
